import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import type { CertificateData } from '../hooks/useCertificate';
// Use simple seal - @react-pdf/renderer has limited SVG gradient support
import { GoldStarburstSealSimple } from './GoldStarburstSeal';

// Design colors from approved mockup
const COLORS = {
  copper: '#C47B2A',
  charcoal: '#2D2D2D',
  gray: '#6B6B6B',
  lightGray: '#F8F8F8',
  dividerGray: '#E0E0E0',
  white: '#FFFFFF',
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: COLORS.white,
    fontFamily: 'Helvetica',
    padding: 40,
    paddingLeft: 50,
    paddingRight: 50,
  },

  // Header - Seal and Client Logo
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  sealContainer: {
    width: 130,
    height: 130,
  },
  clientLogoContainer: {
    alignItems: 'center',
  },
  clientLogo: {
    width: 60,
    height: 60,
    objectFit: 'contain',
  },
  clientLogoText: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.charcoal,
    letterSpacing: 1.5,
    marginTop: 8,
  },

  // Content Section
  content: {
    textAlign: 'center',
    paddingTop: 10,
  },
  copperLine: {
    width: 200,
    height: 2,
    backgroundColor: COLORS.copper,
    alignSelf: 'center',
    marginBottom: 15,
  },
  title: {
    fontFamily: 'Times-Bold',
    fontSize: 24,
    color: COLORS.charcoal,
    textAlign: 'center',
    marginBottom: 15,
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.gray,
    textAlign: 'center',
    marginBottom: 12,
  },
  employeeName: {
    fontFamily: 'Times-BoldItalic',
    fontSize: 28,
    color: COLORS.charcoal,
    textAlign: 'center',
    marginBottom: 12,
  },
  completionText: {
    fontSize: 14,
    color: COLORS.gray,
    textAlign: 'center',
    marginBottom: 15,
  },

  // Details Card
  detailsCard: {
    backgroundColor: COLORS.lightGray,
    borderRadius: 4,
    padding: 15,
    paddingLeft: 25,
    paddingRight: 25,
    marginLeft: 80,
    marginRight: 80,
    marginBottom: 15,
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  detailRowLast: {
    flexDirection: 'row',
    marginBottom: 0,
  },
  detailLabel: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.charcoal,
    width: 70,
  },
  detailValue: {
    fontSize: 13,
    color: COLORS.charcoal,
    flex: 1,
  },

  // Policies Section
  policiesSection: {
    alignItems: 'center',
    marginBottom: 15,
  },
  policiesTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.charcoal,
    marginBottom: 10,
  },
  policyItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  policyBullet: {
    fontSize: 12,
    color: COLORS.copper,
    fontFamily: 'Helvetica-Bold',
    marginRight: 8,
  },
  policyText: {
    fontSize: 12,
    color: COLORS.charcoal,
  },

  // Footer Section
  footerSection: {
    marginTop: 'auto',
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: COLORS.dividerGray,
  },
  footerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 15,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  qrCode: {
    width: 70,
    height: 70,
    marginRight: 20,
  },
  authorityTextContainer: {
    justifyContent: 'center',
  },
  authorityText: {
    fontSize: 11,
    color: COLORS.gray,
    lineHeight: 1.6,
  },
  footerRight: {
    alignItems: 'flex-end',
  },
  certificateIdContainer: {
    flexDirection: 'row',
  },
  certificateIdLabel: {
    fontSize: 11,
    color: COLORS.gray,
  },
  certificateIdValue: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.charcoal,
  },
  verifyUrl: {
    fontSize: 12,
    color: COLORS.copper,
    textAlign: 'center',
    marginTop: 10,
  },
});

interface CertificateTemplateProps {
  data: CertificateData;
  qrCodeDataUrl: string;
  clientLogoUrl?: string;
}

export function CertificateTemplate({ data, qrCodeDataUrl, clientLogoUrl }: CertificateTemplateProps) {
  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        {/* Header - Gold Seal (left) and Client Logo (right) */}
        <View style={styles.header}>
          {/* Gold Starburst Seal - using simple version for @react-pdf compatibility */}
          <View style={styles.sealContainer}>
            <GoldStarburstSealSimple size={130} />
          </View>

          {/* Client Logo - logo image already contains company name */}
          <View style={styles.clientLogoContainer}>
            {clientLogoUrl && (
              <Image
                src={clientLogoUrl}
                style={styles.clientLogo}
              />
            )}
          </View>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Copper Accent Line */}
          <View style={styles.copperLine} />

          {/* Title */}
          <Text style={styles.title}>CERTIFICATE OF COMPLETION</Text>

          {/* Certification Statement */}
          <Text style={styles.subtitle}>This is to certify that</Text>
          <Text style={styles.employeeName}>{data.employeeName}</Text>
          <Text style={styles.completionText}>has successfully completed the training:</Text>
        </View>

        {/* Details Card */}
        <View style={styles.detailsCard}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Module:</Text>
            <Text style={styles.detailValue}>{data.moduleName}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Lesson:</Text>
            <Text style={styles.detailValue}>{data.lessonName}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Score:</Text>
            <Text style={styles.detailValue}>{data.score}%</Text>
          </View>
          <View style={styles.detailRowLast}>
            <Text style={styles.detailLabel}>Date:</Text>
            <Text style={styles.detailValue}>
              {format(data.completionDate, 'd MMMM yyyy, HH:mm')} SAST
            </Text>
          </View>
        </View>

        {/* Policies Acknowledged */}
        {data.policies.length > 0 && (
          <View style={styles.policiesSection}>
            <Text style={styles.policiesTitle}>Policies Acknowledged:</Text>
            {data.policies.map((policy, index) => (
              <View key={index} style={styles.policyItem}>
                <Text style={styles.policyBullet}>•</Text>
                <Text style={styles.policyText}>
                  {policy.name} {policy.reference ? `(${policy.reference})` : ''}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Footer Section */}
        <View style={styles.footerSection}>
          <View style={styles.footerContent}>
            {/* Left: QR Code + Authority Text */}
            <View style={styles.footerLeft}>
              {qrCodeDataUrl && (
                <Image src={qrCodeDataUrl} style={styles.qrCode} />
              )}
              <View style={styles.authorityTextContainer}>
                <Text style={styles.authorityText}>
                  Issued by Ditan Attorney Compliance Portal
                </Text>
                <Text style={styles.authorityText}>
                  on behalf of {data.clientName}
                </Text>
              </View>
            </View>

            {/* Right: Certificate ID */}
            <View style={styles.footerRight}>
              <View style={styles.certificateIdContainer}>
                <Text style={styles.certificateIdLabel}>Certificate ID: </Text>
                <Text style={styles.certificateIdValue}>
                  {data.id.slice(0, 8).toUpperCase()}
                </Text>
              </View>
            </View>
          </View>

          {/* Verification URL */}
          <Text style={styles.verifyUrl}>
            Scan QR code to verify certificate
          </Text>
        </View>
      </Page>
    </Document>
  );
}
